import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { AvatarInitials } from '@/components/common/admin/AvatarInitials';
import { GlassStatusBadge } from '@/components/admin/redesign';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { getLeaseStatusLabel } from '@/utils/admin/residentsList/status';
import { IResidentDetailsResponse } from '@/types/admin/residentDetails';

interface Props {
  resident: Pick<
    IResidentDetailsResponse,
    | 'firstName'
    | 'lastName'
    | 'leaseStatus'
    | 'propertyName'
    | 'buildingUnitNumberSpace'
    | 'email'
    | 'phoneNumber'
    | 'accountId'
  >;
}

export const ResidentDetailsHeader: React.FC<Props> = ({ resident }) => {
  const styles = useStyles();

  const statusVariant = (() => {
    const s = (resident.leaseStatus || '').toLowerCase();
    if (s === 'current') return 'success' as const;
    if (s === 'notice') return 'warning' as const;
    if (s === 'past' || s === 'cancelled') return 'error' as const;
    return 'info' as const;
  })();

  const contactLine = [
    resident.propertyName,
    resident.buildingUnitNumberSpace,
    resident.email,
    resident.phoneNumber,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <View style={styles.container}>
      <View style={styles.row}>
        <AvatarInitials firstName={resident.firstName} lastName={resident.lastName} size={56} />
        <View style={styles.text}>
          <View style={styles.pillRow}>
            <GlassStatusBadge variant={statusVariant} label={getLeaseStatusLabel(resident.leaseStatus)} />
            {resident.accountId ? <GlassStatusBadge variant="neutral" label="Resio Linked" /> : null}
          </View>
          <Text style={styles.name} numberOfLines={1}>
            {resident.firstName} {resident.lastName}
          </Text>
          {contactLine ? (
            <Text style={styles.contact} numberOfLines={2}>
              {contactLine}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      container: {
        paddingVertical: 4,
      },
      row: { flexDirection: 'row', alignItems: 'flex-start', gap: 14 },
      text: { flex: 1, minWidth: 0 },
      pillRow: { flexDirection: 'row', gap: 8, marginBottom: 4, flexWrap: 'wrap' },
      name: { fontSize: 22, fontWeight: '800', color: colors.text.primary, letterSpacing: -0.4 },
      contact: { fontSize: 12.5, color: colors.text.muted, marginTop: 2, lineHeight: 17 },
    }),
  );
